import { useState, useEffect } from "react";
import { api, useAuth } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { toast } from "sonner";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  Save,
  Loader2,
  User,
  Upload,
  ImageIcon,
  X,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getRoleLabel, getRoleColor } from "@/lib/roles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccountPage = () => {
  const { user, login } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    phone: "",
    siret: ""
  });

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    try {
      if (user?.company_id) {
        const res = await api.get(`/companies/${user.company_id}`);
        setCompany(res.data);
        setForm({
          name: res.data.name || "",
          email: res.data.email || "",
          address: res.data.address || "",
          phone: res.data.phone || "",
          siret: res.data.siret || ""
        });
      }
    } catch (error) {
      console.error("Error loading company:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Update first_name
      if (firstName !== (user?.first_name || "")) {
        await api.put("/auth/profile", { first_name: firstName });
      }

      // Update company info
      if (user?.company_id) {
        await api.put(`/companies/${user.company_id}`, form);
      }

      // Refresh user data
      const meRes = await api.get("/auth/me");
      localStorage.setItem("user", JSON.stringify(meRes.data));
      window.location.reload();
      
      toast.success("Informations enregistrées");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file || !user?.company_id) return;
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/companies/${user.company_id}/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo mis à jour");
      loadCompany();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!user?.company_id) return;
    try {
      await api.delete(`/companies/${user.company_id}/logo`);
      toast.success("Logo supprimé");
      loadCompany();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <MobileLayout title="Mon compte" showBack>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </MobileLayout>
    );
  }

  const logoUrl = company?.logo_url
    ? `${BACKEND_URL}${company.logo_url}`
    : null;

  return (
    <MobileLayout title="Mon compte" showBack>
      <div className="px-4 lg:px-6 py-6 space-y-6 animate-fade-in" data-testid="account-page">
        {/* Profile Section */}
        <div className="space-y-4">
          <h3 className="text-sm text-zinc-400 uppercase tracking-wider">Mon profil</h3>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm flex items-center gap-2">
                <User size={14} />
                Prénom
              </Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Votre prénom"
                className="input-dark"
                data-testid="account-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-2">
                <Mail size={14} />
                Email
              </Label>
              <p className="text-zinc-300 text-sm px-1">{user?.email}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-2">
                <Shield size={14} />
                Rôle
              </Label>
              <span className={`inline-block text-xs px-2.5 py-1 rounded font-medium ${getRoleColor(user?.role)}`}>
                {getRoleLabel(user?.role)}
              </span>
            </div>
          </div>
        </div>

        {/* Company Section */}
        {user?.company_id && (
          <div className="space-y-4">
            <h3 className="text-sm text-zinc-400 uppercase tracking-wider">Mon entreprise</h3>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
              {/* Logo */}
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm">Logo</Label>
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <>
                      <img src={logoUrl} alt="Logo" className="w-16 h-16 rounded-lg object-cover border border-zinc-700" />
                      <button
                        onClick={handleDeleteLogo}
                        className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                        data-testid="account-delete-logo"
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border border-dashed border-zinc-700 flex items-center justify-center">
                      <ImageIcon size={20} className="text-zinc-600" />
                    </div>
                  )}
                  <label className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-lg cursor-pointer transition-colors" data-testid="account-upload-logo">
                    {uploadingLogo ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <span>{logoUrl ? "Changer" : "Ajouter"}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleLogoUpload(e.target.files[0])} />
                  </label>
                </div>
              </div>

              {/* Company fields */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm flex items-center gap-2">
                    <Building2 size={14} />
                    Nom
                  </Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-dark"
                    data-testid="account-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm flex items-center gap-2">
                    <Mail size={14} />
                    Email
                  </Label>
                  <Input
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input-dark"
                    data-testid="account-company-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm flex items-center gap-2">
                    <MapPin size={14} />
                    Adresse
                  </Label>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Adresse de l'entreprise"
                    className="input-dark"
                    data-testid="account-company-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm flex items-center gap-2">
                    <Phone size={14} />
                    Téléphone
                  </Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Numéro de téléphone"
                    className="input-dark"
                    data-testid="account-company-phone"
                  />
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <Label className="text-zinc-300 text-sm flex items-center gap-2">
                    <FileText size={14} />
                    SIRET
                  </Label>
                  <Input
                    value={form.siret}
                    onChange={(e) => setForm({ ...form, siret: e.target.value })}
                    placeholder="Numéro SIRET"
                    className="input-dark"
                    data-testid="account-company-siret"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSaveProfile}
          disabled={saving}
          className="btn-primary w-full lg:w-auto"
          data-testid="account-save"
        >
          {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />}
          Enregistrer
        </Button>
      </div>
    </MobileLayout>
  );
};

export default AccountPage;
